import {
  GetIndexHistories200Response,
  GetMarkets200Response,
  GetMarketsSummary200ResponseSummariesInner,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { isInstrumentTypeEnabled } from "../../config/features";
import { optionsEnabledAssets } from "../../constants/assets";
import { IMarket } from "../../contexts/MarketContext";
import { getAssetShortName } from "../../utils/asset/assets";
import { apiNum } from "../../utils/math";
import { getConfigPricePrecision } from "./utils";
import {
  IOptionMarketData,
  IPerpetualMarketData,
} from "./MarketsTable";
import {
  ISectionData,
  MarketFiltersEnum,
} from "./CommandModal.types";
import {
  normalizeMarketCategory,
  perpCategoryFilters,
  sortWatchlist,
} from "./CommandModal.helpers";

export type SearchResult = GetMarketsSummary200ResponseSummariesInner & {
  display_name?: string;
  market_category?: string;
};

export const computeSectionsData = (params: {
  searchResults: SearchResult[];
  watchlist: IMarket[];
  market: IMarket;
  marketFilter: MarketFiltersEnum;
  assetIndexHistories: GetIndexHistories200Response | undefined;
  t: (key: string) => string;
  /** Scoped to the `tradingTypes` i18n block — names the per-vertical sections. */
  tTradingType: (key: string) => string;
  searchText: string;
  uniqueSpotAssets: string[];
  spotMarketsData: GetMarkets200Response[] | undefined;
}): ISectionData[] => {
  const {
    searchResults,
    watchlist,
    market,
    marketFilter,
    assetIndexHistories,
    t,
    tTradingType,
    searchText,
    uniqueSpotAssets,
    spotMarketsData,
  } = params;
    const optionsData: IOptionMarketData[] = [...searchResults]
      .filter((d) => !!optionsEnabledAssets[d.asset])
      .map((d) => {
        const putOI = Number(d.option_info?.open_interest?.puts);
        const callOI = Number(d.option_info?.open_interest?.calls);
        const putCallRatio = putOI && callOI ? putOI / callOI : 0;

        // OI in USD needs a quoted index price. `/markets-summary` sends null
        // for an unknown one, and `Number(null) * total` reported $0 of open
        // interest (then `|| 0` below made every unknown a confident 0).
        const indexPrice = apiNum(d.index_price);
        const openInterestTotal = apiNum(d.option_info?.open_interest?.total);
        const openInterestUSD =
          indexPrice !== undefined && indexPrice > 0 && openInterestTotal !== undefined
            ? indexPrice * openInterestTotal
            : undefined;

        return {
          id: `OPTION-${d.asset}`,
          asset: d.asset,
          putCallRatio,
          oi: openInterestUSD,
          dailyVolume: apiNum(d.option_info?.daily_volume),
          isWatchlist: watchlist.some(
            (w) =>
              w.asset === d.asset &&
              w.derivative === InstrumentTypeResponse.Option
          ),
          isCurrentMarket:
            market.asset === d.asset &&
            market.derivative === InstrumentTypeResponse.Option,
          pricePrecision: getConfigPricePrecision(
            d.asset,
            d.option_info?.amount_step,
            d.option_info?.price_step
          ),
          indexHistory: [],
        };
      })
      .filter((d) =>
        marketFilter === MarketFiltersEnum.WATCHLIST ? d.isWatchlist : true
      )
      .sort(sortWatchlist);

    const allResults = [...searchResults]
      .filter((d) => !!d.perpetual_info)
      .map((d) => {
        // `apiNum` keeps a genuine "0" distinct from an absent field. Truthiness
        // cannot: several upstreams defaulted an unknown 24h change to the
        // STRING "0", which is truthy, so every unmeasured market rendered a
        // green, confident "0.00%" while the trading header for the same market
        // showed "-". Absent stays absent, and the cell renders "-".
        const change =
          apiNum(d.perpetual_info?.mark_daily_change) ?? apiNum(d.index_daily_change);
        const currentPrice =
          apiNum(d.perpetual_info?.mark_price) ?? apiNum(d.index_price);
        // The reference must be a REAL 24h price, the same rule the trading
        // header uses -- otherwise the two disagree about the same market.
        // Deriving it as `current - change` looks equivalent but is not: when an
        // upstream defaults an unknown change to "0" (as the deployed backend
        // still does), the derivation hands back `current`, and 0/current is a
        // confident "0.00%" for a market nobody has priced. No reference, no
        // percentage.
        // Cast: /markets-summary returns mark_price_24h_ago on perpetual_info
        // (verified live), but the OpenAPI schema omits it and codegen-api is
        // generated, not hand-edited -- same reason market_category is cast above.
        const price24hAgo = apiNum(
          (d.perpetual_info as any)?.mark_price_24h_ago
        );
        const dailyChange =
          change !== undefined && price24hAgo !== undefined && price24hAgo > 0
            ? change / price24hAgo
            : undefined;
        const isPrelaunch =
          !!d.perpetual_info?.pre_launch ||
          (d as any).market_category === "Pre-IPO";
        return {
          id: `PERPETUAL-${d.asset}`,
          asset: d.asset,
          market: `${getAssetShortName(d.asset) || d.asset}-USD`,
          // Absent mark stays absent ("-"), never a "$0.00" quote.
          price: d.perpetual_info?.mark_price ?? undefined,
          dailyChange,
          dailyVolume: apiNum(d.perpetual_info?.daily_volume),
          isWatchlist: watchlist.some(
            (w) =>
              w.asset === d.asset &&
              w.derivative === InstrumentTypeResponse.Perpetual
          ),
          isPrelaunch,
          category: normalizeMarketCategory(
            (d as any).market_category,
            isPrelaunch
          ),
          isCurrentMarket:
            market.asset === d.asset &&
            market.derivative === InstrumentTypeResponse.Perpetual,
          pricePrecision: getConfigPricePrecision(
            d.asset,
            d.perpetual_info?.amount_step,
            d.perpetual_info?.price_step
          ),
          indexHistory:
            assetIndexHistories?.assets_history?.find(
              (h) => h.asset === d.asset
            )?.history || [],
        };
      })
      .filter((d) =>
        marketFilter === MarketFiltersEnum.WATCHLIST ? d.isWatchlist : true
      )
      .sort(sortWatchlist);

    const sections: ISectionData[] = [];

    if (
      isInstrumentTypeEnabled(InstrumentTypeResponse.Perpetual) &&
      allResults.length &&
      (marketFilter === MarketFiltersEnum.ALL_MARKETS ||
        marketFilter === MarketFiltersEnum.WATCHLIST ||
        perpCategoryFilters.includes(marketFilter as MarketFiltersEnum))
    ) {
      const visibleCategories = perpCategoryFilters.includes(
        marketFilter as MarketFiltersEnum
      )
        ? [marketFilter as MarketFiltersEnum]
        : perpCategoryFilters;

      visibleCategories.forEach((category) => {
        const categoryData = allResults.filter((d) => d.category === category);
        if (!categoryData.length) return;

        sections.push({
          name: t(category),
          instrumentType: InstrumentTypeResponse.Perpetual,
          isPrelaunch: category === MarketFiltersEnum.PRE_IPO,
          data: categoryData,
        });
      });
    }

    if (
      isInstrumentTypeEnabled(InstrumentTypeResponse.Option) &&
      optionsData.length &&
      (marketFilter === MarketFiltersEnum.ALL_MARKETS ||
        marketFilter === MarketFiltersEnum.WATCHLIST)
    ) {
      sections.push({
        name: tTradingType("options"),
        instrumentType: InstrumentTypeResponse.Option,
        isPrelaunch: false,
        data: optionsData,
      });
    }

    // Spot markets section
    const filteredSpotAssets = searchText
      ? uniqueSpotAssets.filter((asset) => asset.toLowerCase().includes(searchText.toLowerCase()))
      : uniqueSpotAssets;
    const spotResults: IPerpetualMarketData[] = filteredSpotAssets
      .map((asset) => {
        const mkt = spotMarketsData?.find((d) => d.underlying_asset === asset);
        return {
          id: `SPOT-${asset}`,
          asset,
          market: `${getAssetShortName(asset) || asset}-USDC`,
          price: mkt?.mark_price || mkt?.index_price || undefined,
          // No 24h source exists for spot on this endpoint. A hardcoded 0 was a
          // claim every spot market is always flat; undefined renders "-".
          dailyChange: undefined,
          // ...and no volume source either. A hardcoded 0 made the same claim
          // one line further down: that every spot market traded nothing.
          dailyVolume: undefined,
          isWatchlist: watchlist.some(
            (w) =>
              w.asset === asset &&
              w.derivative === InstrumentTypeResponse.Spot
          ),
          isPrelaunch: false,
          isCurrentMarket:
            market.asset === asset &&
            market.derivative === InstrumentTypeResponse.Spot,
          pricePrecision: getConfigPricePrecision(
            asset,
            mkt?.amount_step,
            mkt?.price_step
          ),
          indexHistory: [],
        };
      })
      .filter((d) =>
        marketFilter === MarketFiltersEnum.WATCHLIST ? d.isWatchlist : true
      )
      .sort(sortWatchlist);

    if (
      isInstrumentTypeEnabled(InstrumentTypeResponse.Spot) &&
      spotResults.length &&
      (marketFilter === MarketFiltersEnum.ALL_MARKETS || marketFilter === MarketFiltersEnum.WATCHLIST)
    ) {
      sections.push({
        name: tTradingType("perpetual_spot"),
        instrumentType: InstrumentTypeResponse.Spot,
        isPrelaunch: false,
        data: spotResults,
      });
    }

    return sections;
};
